import {
  Body,
  Ecliptic,
  EclipticGeoMoon,
  GeoVector,
  MakeTime,
  Observer,
  SearchRiseSet,
  SunPosition,
} from 'astronomy-engine';
import type { Planet } from '@astrally/shared';
import { norm360 } from '../math/angles.js';
import type { EclipticState, EphemerisAdapter } from './adapter.js';
import { SiderealTime } from 'astronomy-engine';

const BODY_MAP: Partial<Record<Planet, Body>> = {
  sun: Body.Sun,
  moon: Body.Moon,
  mercury: Body.Mercury,
  venus: Body.Venus,
  mars: Body.Mars,
  jupiter: Body.Jupiter,
  saturn: Body.Saturn,
  uranus: Body.Uranus,
  neptune: Body.Neptune,
  pluto: Body.Pluto,
};

/** Geocentric apparent ecliptic-of-date longitude/latitude for one instant. */
function eclipticOfDate(planet: Planet, utc: Date): { lon: number; lat: number } {
  const time = MakeTime(utc);
  if (planet === 'sun') {
    const s = SunPosition(time);
    return { lon: norm360(s.elon), lat: s.elat };
  }
  if (planet === 'moon') {
    const m = EclipticGeoMoon(time);
    return { lon: norm360(m.lon), lat: m.lat };
  }
  if (planet === 'rahu' || planet === 'ketu') {
    const lon = meanLunarNode(time.tt / 36525);
    return { lon: planet === 'rahu' ? lon : norm360(lon + 180), lat: 0 };
  }
  const body = BODY_MAP[planet];
  if (body === undefined) throw new Error(`Unsupported body: ${planet}`);
  const vec = GeoVector(body, time, true);
  const ecl = Ecliptic(vec);
  return { lon: norm360(ecl.elon), lat: ecl.elat };
}

/**
 * Mean ascending lunar node (Meeus, Astronomical Algorithms ch. 47).
 * T = Julian centuries TT since J2000. Node convention recorded in chart
 * metadata as "mean"; a true-node option can be added to the adapter later.
 */
export function meanLunarNode(T: number): number {
  const omega =
    125.0445479 -
    1934.1362891 * T +
    0.0020754 * T * T +
    (T * T * T) / 467441 -
    (T * T * T * T) / 60616000;
  return norm360(omega);
}

/** Mean obliquity of the ecliptic (Meeus 22.2), degrees. */
export function meanObliquity(T: number): number {
  const seconds = 21.448 - 46.815 * T - 0.00059 * T * T + 0.001813 * T * T * T;
  return 23 + 26 / 60 + seconds / 3600;
}

export class AstronomyEngineAdapter implements EphemerisAdapter {
  readonly id = 'astronomy-engine@2';

  position(planet: Planet, utc: Date): EclipticState {
    const { lon, lat } = eclipticOfDate(planet, utc);
    // Numerical speed over ±12h — plenty for retrograde detection and
    // applying/separating logic at natal-chart precision.
    const before = eclipticOfDate(planet, new Date(utc.getTime() - 43200000)).lon;
    const after = eclipticOfDate(planet, new Date(utc.getTime() + 43200000)).lon;
    let delta = norm360(after - before);
    if (delta > 180) delta -= 360;
    return { lon, lat, speed: delta };
  }

  obliquity(utc: Date): number {
    return meanObliquity(this.julianCenturiesTT(utc));
  }

  siderealTimeHours(utc: Date): number {
    return SiderealTime(MakeTime(utc));
  }

  julianCenturiesTT(utc: Date): number {
    return MakeTime(utc).tt / 36525;
  }

  sunrise(latitude: number, longitude: number, utcFrom: Date): Date | null {
    const observer = new Observer(latitude, longitude, 0);
    const t = SearchRiseSet(Body.Sun, observer, +1, MakeTime(utcFrom), 1.2);
    return t ? t.date : null;
  }
}
